import { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useBiens } from "@/hooks/useBiens";
import { isForSale } from "@/lib/transaction";
import { DataTable, type ColumnDef, type SortState } from "@/v2/components/DataTable";
import type { BienRow } from "@/schemas/bien";
import { BadgeEuro, ChevronLeft, ChevronRight, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { getPublicationStage, publicationSortValue } from "@/v2/lib/bienLifecycle";
import { disponibiliteLabel } from "@/v2/lib/disponibiliteLabel";
import { checkElsStates } from "@/v2/components/CheckElsBadges";
import { setListOrder } from "@/hooks/useListNavigation";

/**
 * Catalogue VENTE (V1 DRAFT — chantier Catalogue Sales 2026-07-12).
 *
 * Liste des biens en vente (`transaction = "vente"`), miroir volontairement
 * SIMPLIFIÉ du catalogue location (BiensCataloguePage) : chips statut +
 * recherche libre + table + pagination. Pas de ColumnPickerModal, pas
 * d'export XLS, pas de filtres région/chambres/meublé/company/codes postaux —
 * les colonnes et tris définitifs seront décidés avec l'équipe Sales.
 *
 * Colonnes en ColumnDef inline (pas de registry) tant que le schéma de la
 * page n'est pas figé.
 */

const PAGE_SIZE = 50;

// Clé localStorage du tri Catalogue Vente — persiste au retour depuis une fiche.
const VENTE_SORT_LS_KEY = "skyline-catalogue-vente-sort-v1";

function readStoredSort(key: string): SortState | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.key === "string" && (parsed.dir === "asc" || parsed.dir === "desc")) {
      return parsed as SortState;
    }
    return null;
  } catch {
    return null;
  }
}

type StatusFilter = "all" | "a-vendre" | "sous-option" | "vendu";

/** DISPONIBLE + EN PRÉPARATION (legacy) = "À vendre" côté Sales. */
const isAVendre = (b: BienRow): boolean => {
  const d = (b.disponibilite || "").toUpperCase();
  return d === "DISPONIBLE" || d === "EN PRÉPARATION";
};

const STATUS_FILTERS: { key: StatusFilter; labelKey: string; matches: (b: BienRow) => boolean }[] = [
  { key: "all", labelKey: "common.all", matches: () => true },
  { key: "a-vendre", labelKey: "vente.catalogue.aVendre", matches: isAVendre },
  { key: "sous-option", labelKey: "v2.catalogue.chips.sousOption", matches: (b) => !!b.sousOption },
  { key: "vendu", labelKey: "v2.catalogue.chips.vendu", matches: (b) => (b.disponibilite || "").toUpperCase() === "VENDU" },
];

const num = (v?: string | null) => parseFloat(v || "0") || 0;

/** Formate un prix de vente "1 280 000 €" (séparateur de milliers fr-BE,
 *  même convention que installationDisplay.ts). "" si absent/non numérique. */
const formatPrix = (v?: string | null): string => {
  if (!v || !v.trim()) return "";
  const n = parseFloat(v);
  if (Number.isNaN(n)) return "";
  return `${n.toLocaleString("fr-BE")} €`;
};

/** Chip Statut d'un bien VENTE — même style que dispoChip du catalogue
 *  location, mais DISPONIBLE / EN PRÉPARATION s'affiche "À vendre" (le
 *  vocabulaire "Disponible" est un réflexe location). VENDU garde le libellé
 *  standard (disponibiliteLabel). Badge orange "Sous option" en overlay. */
const venteStatutCell = (b: BienRow, t: (k: string) => string) => {
  const aVendre = isAVendre(b);
  return (
    <span className="inline-flex items-center justify-center gap-1.5 flex-wrap">
      {b.disponibilite ? (
        <span
          className={cn(
            "inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider",
            aVendre ? "bg-success/15 text-success" : "bg-gray-100 text-text-muted",
          )}
        >
          {aVendre ? t("vente.catalogue.aVendre") : disponibiliteLabel(b.disponibilite, t)}
        </span>
      ) : (
        <span className="text-text-muted">—</span>
      )}
      {b.sousOption && (
        <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-medium bg-warning/10 text-warning whitespace-nowrap">
          {t("biens.sousOption.badge")}
        </span>
      )}
    </span>
  );
};

/** Copie du pebChip du catalogue location : lettre PEB, fond vert si la date
 *  du certificat est encore valide, rouge sinon (même règle que Check ELS). */
const pebChip = (b: BienRow) => {
  if (!b.classePeb) return <span className="text-danger">—</span>;
  const valid = checkElsStates(b).peb === "success";
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${
        valid ? "bg-success/15 text-success" : "bg-danger/15 text-danger"
      }`}
    >
      {b.classePeb}
    </span>
  );
};

const LIFE_COLORS: Record<string, string> = {
  danger: "bg-danger/15 text-danger",
  warning: "bg-warning/15 text-warning",
  accent: "bg-accent/15 text-accent",
  success: "bg-success/15 text-success",
  muted: "bg-gray-100 text-text-muted",
};

export function BiensVentePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: allBiens, isLoading } = useBiens();

  // Catalogue VENTE : uniquement les biens `transaction = "vente"`. Les biens
  // à transaction NULL (historiques) restent dans le catalogue location.
  const biens = useMemo(
    () => (allBiens || []).filter((b) => isForSale(b.transaction)),
    [allBiens],
  );

  const [status, setStatus] = useState<StatusFilter>("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);

  // Tri contrôlé : appliqué par le parent AVANT la pagination (sinon le
  // DataTable ne trierait que les 50 lignes visibles).
  // Persistance localStorage (par navigateur) → survit au retour depuis une fiche.
  const [sort, setSort] = useState<SortState | null>(() => readStoredSort(VENTE_SORT_LS_KEY));
  const saveSort = (next: SortState | null) => {
    setSort(next);
    try {
      if (next) localStorage.setItem(VENTE_SORT_LS_KEY, JSON.stringify(next));
      else localStorage.removeItem(VENTE_SORT_LS_KEY);
    } catch {
      /* quota / privacy mode → ignore */
    }
  };

  const counts = useMemo(() => {
    const c: Record<StatusFilter, number> = { all: 0, "a-vendre": 0, "sous-option": 0, vendu: 0 };
    for (const b of biens) {
      for (const f of STATUS_FILTERS) {
        if (f.matches(b)) c[f.key]++;
      }
    }
    return c;
  }, [biens]);

  // Recherche libre appliquée AVANT pagination (cherche sur tous les biens,
  // pas seulement la page courante — même règle que le catalogue location).
  const filtered = useMemo(() => {
    let list = biens;
    if (status !== "all") {
      const f = STATUS_FILTERS.find((s) => s.key === status);
      if (f) list = list.filter(f.matches);
    }
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((b) =>
        [b.referenceInterne, b.reference, b.id, b.adresse, b.ville, b.projet, b.codeImmoweb, b.referenceExterne]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(q),
      );
    }
    return list;
  }, [biens, status, search]);

  // Colonnes DRAFT inline — le registry + picker viendront quand le schéma
  // de la page sera validé par l'équipe Sales.
  const columns: ColumnDef<BienRow>[] = [
    {
      key: "ref",
      label: t("biens.reference"),
      width: 130,
      sortValue: (b) => b.referenceInterne || b.reference || "",
      render: (b) => (
        <span className="font-medium text-text-primary">
          {b.referenceInterne || b.reference || "—"}
        </span>
      ),
    },
    {
      key: "adresse",
      label: t("common.address"),
      width: 230,
      sortValue: (b) => b.adresse || "",
      render: (b) => (
        <div className="min-w-0">
          <div className="text-text-primary truncate">{b.adresse || "—"}</div>
          <div className="text-xs text-text-muted truncate">
            {[b.codePostal, b.ville].filter(Boolean).join(" ")}
          </div>
        </div>
      ),
    },
    {
      key: "statut",
      label: t("common.status"),
      width: 170,
      align: "center",
      sortValue: (b) => b.disponibilite || "",
      render: (b) => venteStatutCell(b, t),
    },
    {
      key: "prix",
      label: t("vente.catalogue.col.prix"),
      width: 130,
      align: "center",
      sortValue: (b) => num(b.prix),
      render: (b) =>
        formatPrix(b.prix) ? (
          <span className="font-medium text-text-primary whitespace-nowrap">{formatPrix(b.prix)}</span>
        ) : (
          <span className="text-text-muted">—</span>
        ),
    },
    {
      key: "surface",
      label: t("biens.surface"),
      width: 100,
      align: "center",
      sortValue: (b) => num(b.surfaceHabitable),
      render: (b) =>
        b.surfaceHabitable ? `${b.surfaceHabitable} m²` : <span className="text-text-muted">—</span>,
    },
    {
      key: "peb",
      label: t("biens.columns.classePeb"),
      width: 70,
      align: "center",
      sortValue: (b) => b.classePeb || "",
      render: (b) => pebChip(b),
    },
    {
      key: "publication",
      label: t("v2.catalogue.columns.publication"),
      width: 180,
      align: "center",
      sortValue: (b) => publicationSortValue(getPublicationStage(b).stage),
      render: (b) => {
        const ps = getPublicationStage(b);
        return (
          <span
            className={cn(
              "inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider",
              LIFE_COLORS[ps.color],
            )}
          >
            {t(ps.labelKey)}
          </span>
        );
      },
    },
  ];
  const tableMinWidth = columns.reduce((sum, c) => sum + (c.width ?? 120), 0);

  // Tri appliqué sur la liste filtrée COMPLÈTE (avant pagination) — même
  // logique que le catalogue location.
  const sortedFiltered = useMemo(() => {
    if (!sort) return filtered;
    const col = columns.find((c) => c.key === sort.key);
    if (!col?.sortValue) return filtered;
    const cmp = (a: BienRow, b: BienRow) => {
      const va = col.sortValue!(a);
      const vb = col.sortValue!(b);
      const empty = (v: typeof va) => v === null || v === undefined || v === "";
      if (empty(va) && empty(vb)) return 0;
      if (empty(va)) return 1;
      if (empty(vb)) return -1;
      if (va instanceof Date && vb instanceof Date) return va.getTime() - vb.getTime();
      if (typeof va === "number" && typeof vb === "number") return va - vb;
      return String(va).localeCompare(String(vb), "fr", { numeric: true });
    };
    const copy = [...filtered];
    copy.sort((a, b) => (sort.dir === "asc" ? cmp(a, b) : cmp(b, a)));
    return copy;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, sort]);

  // Reset page quand filtre / recherche / tri change
  useEffect(() => {
    setPage(0);
  }, [search, status, sort]);

  // Persiste l'ordre filtré + trié pour la nav prev/next dans la fiche.
  // Clé DISTINCTE "biens-vente" (le catalogue location garde "biens").
  useEffect(() => {
    setListOrder("biens-vente", sortedFiltered.map((b) => b.id));
  }, [sortedFiltered]);

  const pageCount = Math.max(1, Math.ceil(sortedFiltered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const rows = sortedFiltered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);
  const startIdx = safePage * PAGE_SIZE + 1;
  const endIdx = Math.min(startIdx + rows.length - 1, sortedFiltered.length);

  return (
    <div className="p-4 md:p-6">
      <div className="mb-5">
        <h1 className="text-2xl font-semibold text-text-primary flex items-center gap-2">
          <BadgeEuro size={22} className="text-accent" />
          {t("v2.nav.catalogueVente")}
        </h1>
        <p className="text-sm text-text-muted mt-1">{t("vente.catalogue.subtitle")}</p>
      </div>

      {/* Chips filtre statut */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {STATUS_FILTERS.map((f) => {
          const isActive = status === f.key;
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => {
                setStatus(f.key);
                setPage(0);
              }}
              className={cn(
                "px-3 py-1.5 rounded-md text-xs font-medium cursor-pointer transition-colors border",
                isActive
                  ? "bg-accent-light text-accent border-accent"
                  : "bg-surface text-text-secondary border-border hover:border-accent/30",
              )}
            >
              <span>{t(f.labelKey)}</span>
              <span
                className={cn(
                  "ml-1.5",
                  f.key === "sous-option" ? "text-warning" : "text-text-muted",
                )}
              >
                ({counts[f.key]})
              </span>
            </button>
          );
        })}
      </div>

      {/* Recherche libre — appliquée AVANT pagination. */}
      <div className="mb-3 relative max-w-md">
        <Search
          size={14}
          className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none"
        />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("v2.catalogue.searchPlaceholder")}
          className="w-full pl-8 pr-3 py-1.5 text-sm rounded-md border border-border bg-surface outline-none"
        />
      </div>

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(b) => b.id}
        onRowClick={(b) => navigate(`/v2/biens/${b.id}`)}
        searchable={false}
        sort={sort}
        onSortChange={saveSort}
        isLoading={isLoading}
        minWidth={tableMinWidth}
        fillViewport
        emptyLabel={search ? t("vente.catalogue.emptySearch") : t("vente.catalogue.empty")}
      />

      {/* Pagination */}
      {sortedFiltered.length > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs text-text-muted">
            {t("v2.catalogue.paginationRange", { start: startIdx, end: endIdx, total: sortedFiltered.length })}
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={safePage === 0}
              className="p-1.5 rounded-md border border-border hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              <ChevronLeft size={14} />
            </button>
            <span className="text-xs px-3 py-1.5 bg-surface border border-border rounded-md font-medium">
              {t("v2.catalogue.pageOf", { page: safePage + 1, total: pageCount })}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              disabled={safePage >= pageCount - 1}
              className="p-1.5 rounded-md border border-border hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
